import { useEffect, useRef } from 'react';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'] as const;
const LAST_ACTIVITY_KEY = 'idle_logout_last_activity';
const ACTIVITY_THROTTLE_MS = 5_000;
const CHECK_INTERVAL_MS = 30_000;
const DEFAULT_TIMEOUT_MINUTES = 60;

function idleTimeoutMs(): number {
  const mins = parseInt(import.meta.env.VITE_SESSION_TIMEOUT || String(DEFAULT_TIMEOUT_MINUTES), 10);
  return (Number.isFinite(mins) && mins > 0 ? mins : DEFAULT_TIMEOUT_MINUTES) * 60 * 1000;
}

/**
 * Universal inactivity logout for EVERY authenticated role — the platform previously left staff with
 * no idle timeout at all, a real risk on shared front-desk workstations. This is distinct from the
 * opt-in PIN screen-lock (AppLockContext, clinic_admin/doctor only): idle here ENDS the session.
 *
 * Activity is recorded in a SHARED `localStorage` timestamp, so work in any tab counts for all of
 * them (a doctor typing in tab B never gets logged out by an idle tab A). When the whole browser has
 * been idle past `VITE_SESSION_TIMEOUT` minutes, `onIdle` runs — AuthContext's signOut, which also
 * broadcasts a logout so every tab drops to /login together.
 *
 * @param active pass whether a user is currently logged in; listeners attach only then.
 * @param onIdle a STABLE callback (memoize it) invoked once the idle threshold is crossed.
 */
export function useIdleLogout(active: boolean, onIdle: () => void): void {
  const lastMarkRef = useRef<number>(0);

  useEffect(() => {
    if (!active) return;

    const markActivity = () => {
      const now = Date.now();
      if (now - lastMarkRef.current < ACTIVITY_THROTTLE_MS) return;
      lastMarkRef.current = now;
      try {
        localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
      } catch {
        /* storage unavailable — the interval below still bounds the session via in-memory time */
      }
    };

    const readLastActivity = (): number => {
      try {
        const v = localStorage.getItem(LAST_ACTIVITY_KEY);
        return v ? parseInt(v, 10) : Date.now();
      } catch {
        return Date.now();
      }
    };

    // Seed activity on mount so a freshly-loaded session isn't treated as instantly idle.
    lastMarkRef.current = 0;
    markActivity();

    const check = () => {
      if (Date.now() - readLastActivity() >= idleTimeoutMs()) {
        onIdle();
      }
    };

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActivity, { passive: true }));
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActivity));
      clearInterval(timer);
    };
  }, [active, onIdle]);
}
